import { useEffect, useMemo, useState } from 'react'

import useCart from '@context/useCartContext'

import type { SubscriptionBoxSellingPlanData } from '@lib/types'
import type { GetStartedPageContent } from '@lib/types/get-started-page'
import { FALLBACK_PAGE_CONTENT } from '@lib/types/get-started-page'

import AddToCart from '@components/product/ProductView/AddToCart'

import MobilePlanCard from './MobilePlanCard'
import type { GiftOffer } from './MobilePlanCard'

type PlanWithTag = SubscriptionBoxSellingPlanData & {
	tag: string | null
	benefits?: string[]
	bonus?: { text: string; subtext?: string } | null
}

export type PlanSelectionInfo = {
	sellingPlanId: string
	tag: string | null
	name?: string
}

export default function MobilePlanSelection({
	plans,
	onAddToCart,
	offerSubheadline,
	annualOfferCallout,
	annualCouponCode,
	annualTagSuffix,
	annualGiftOffer,
	pageContent: pageContentProp
}: {
	plans: PlanWithTag[]
	onAddToCart: (plan: PlanSelectionInfo) => void
	offerSubheadline?: string | null
	/** Short description shown beside the coupon code */
	annualOfferCallout?: string | null
	/** Coupon code to display and copy */
	annualCouponCode?: string | null
	/** Short tag pill suffix for discount-type offers, e.g. "20% off 1st box" */
	annualTagSuffix?: string | null
	/** Gift product data for free_gift-type offers */
	annualGiftOffer?: GiftOffer | null
	pageContent?: GetStartedPageContent
}) {
	const pageContent = pageContentProp ?? FALLBACK_PAGE_CONTENT
	const [selectedPlanId, setSelectedPlanId] = useState<string>('')
	const [hasExplicitlySelected, setHasExplicitlySelected] = useState(false)
	const [loading, setLoading] = useState(false)
	const { items } = useCart()

	const selectedPlan = plans.find((p) => p.sellingPlanId === selectedPlanId)

	const isSelectedPlanInCart = useMemo(() => {
		if (!items || !selectedPlan) return false
		return items.some(
			(item) =>
				item.merchandise?.id === selectedPlan.variantId &&
				item.sellingPlanAllocation?.sellingPlan?.id === selectedPlan.sellingPlanId
		)
	}, [items, selectedPlan])

	// If the selected plan is already in cart (e.g. back-navigation), treat as explicitly selected
	useEffect(() => {
		if (isSelectedPlanInCart) setHasExplicitlySelected(true)
	}, [isSelectedPlanInCart])

	const handleNextClick = () => {
		if (isSelectedPlanInCart && selectedPlan) {
			onAddToCart({
				sellingPlanId: selectedPlan.sellingPlanId,
				tag: selectedPlan.tag ?? null,
				name: selectedPlan.name
			})
		}
	}

	return (
		<div className="flex flex-col items-center w-full">
			{/* Header */}
			<div className="flex flex-col gap-1 px-4 mb-8 text-center">
				<h1 className="text-2xl md:text-3xl font-serif font-medium text-center">{pageContent.planSelectionHeading}</h1>
				<p className="mt-2 text-sm md:text-base text-center text-gray-500 font-medium">
					{offerSubheadline ?? pageContent.planSelectionFallbackSubheadline}
				</p>
			</div>

			{/* Stacked plan cards - all fully visible */}
			<div className="flex flex-col gap-4 w-full max-w-2xl mx-auto pb-20">
				{plans.map((subscription, index) => (
					<MobilePlanCard
						key={subscription.sellingPlanId}
						subscription={subscription}
						tag={subscription.tag}
						benefits={subscription.benefits}
						bonus={subscription.bonus}
						offerCallout={index === 0 ? annualOfferCallout : null}
						couponCode={index === 0 ? annualCouponCode : null}
						tagSuffix={index === 0 ? annualTagSuffix : null}
						giftOffer={index === 0 ? annualGiftOffer : null}
						isSelected={subscription.sellingPlanId === selectedPlanId}
						onSelect={() => {
							setSelectedPlanId(subscription.sellingPlanId)
							setHasExplicitlySelected(true)
						}}
						pageContent={pageContent}
					/>
				))}
			</div>

			{/* Fixed NEXT button at bottom */}
			{plans.length > 0 && (
				<div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)]">
					<div className="px-4 pt-3 pb-3 max-w-2xl mx-auto">
						{(() => {
							if (!hasExplicitlySelected) {
								return (
									/* No plan chosen yet — label IS the instruction */
									<button
										type="button"
										disabled
										aria-disabled="true"
										className="button button-action btn-lg w-full"
									>
										Select a plan to continue
									</button>
								)
							}
							if (isSelectedPlanInCart) {
								return (
									/* Plan already in cart — proceed to checkout */
									<button
										type="button"
										onClick={handleNextClick}
										className="button button-action btn-lg w-full"
									>
										{pageContent.ctaContinueToCheckout}
									</button>
								)
							}
							if (selectedPlan) {
								return (
									/* Plan chosen but not yet in cart — add to cart */
									<AddToCart
										product={{
											id: selectedPlan.productId,
											name: selectedPlan.name,
											price: {
												amount: selectedPlan.pricePerMonth.toString(),
												currencyCode: selectedPlan.currencyCode
											},
											vendor: 'birchbox',
											availableForSale: true,
											requiresSellingPlan: true
										}}
										loading={loading}
										setLoading={setLoading}
										sellingPlanId={selectedPlan.sellingPlanId}
										variant={{ id: selectedPlan.variantId, availableForSale: true }}
										ctaText={pageContent.ctaContinueToCheckout}
										onAddToCart={() =>
											onAddToCart({
												sellingPlanId: selectedPlan.sellingPlanId,
												tag: selectedPlan.tag ?? null,
												name: selectedPlan.name
											})
										}
										lineItemAttributes={[{ key: 'source', value: 'get_started_flow' }]}
										className="button button-action btn-lg w-full"
									/>
								)
							}
							return null
						})()}
					</div>
				</div>
			)}
		</div>
	)
}
